import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { mockTranscripts, getStatusLabel, getStatusColor, getStatusBgColor, getTypeLabel, formatDuration } from '../data/mockData';
import { formatDateTime, formatNumber } from '../utils/persian';

export const HistoryView: React.FC = () => {
  const navigate = useNavigate();
  const { isDemo } = useAuth();
  const [filter, setFilter] = React.useState<'all' | 'completed' | 'processing'>('all');

  const filtered = filter === 'all'
    ? mockTranscripts
    : mockTranscripts.filter(t => t.status === filter);

  return (
    <div style={styles.container}>
      {isDemo && (
        <div style={styles.demoBanner}>
          <span style={styles.demoIcon}>🔧</span>
          <span style={styles.demoText}>حالت نمایشی - سرویس پشتیبان فعال نیست</span>
        </div>
      )}

      <div style={styles.header}>
        <h2 style={styles.title}>تاریخچه رونویسی‌ها</h2>
        <p style={styles.subtitle}>{formatNumber(mockTranscripts.length)} مورد</p>
      </div>

      <div style={styles.filterRow}>
        {(['all', 'completed', 'processing'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{
              ...styles.filterButton,
              ...(filter === f ? styles.filterButtonActive : {}),
            }}
          >
            {f === 'all' ? 'همه' : getStatusLabel(f)}
          </button>
        ))}
      </div>

      <div style={styles.list}>
        {filtered.map((t) => (
          <div
            key={t.id}
            onClick={() => navigate(`/transcript/${t.id}`)}
            style={styles.card}
          >
            <div style={styles.cardHeader}>
              <div style={styles.cardTitleRow}>
                <span style={styles.typeBadge}>{getTypeLabel(t.type)}</span>
                <span style={{
                  ...styles.statusBadge,
                  backgroundColor: getStatusBgColor(t.status),
                  color: getStatusColor(t.status),
                }}>
                  {getStatusLabel(t.status)}
                </span>
              </div>
              <p style={styles.cardTitle}>{t.title}</p>
            </div>
            <div style={styles.cardFooter}>
              <span style={styles.cardMeta}>{formatDateTime(t.created_at)}</span>
              <span style={styles.cardMeta}>{formatDuration(t.duration_sec)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    paddingBottom: '1rem',
  },
  demoBanner: {
    backgroundColor: '#fff8e1',
    border: '1px solid #ffe082',
    borderRadius: '8px',
    padding: '0.5rem 1rem',
    marginBottom: '1rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.8rem',
    color: '#f57c00',
  },
  demoIcon: {
    fontSize: '1.2rem',
  },
  demoText: {
    flex: 1,
  },
  header: {
    marginBottom: '1rem',
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: '#1a1a1a',
    margin: '0 0 0.25rem 0',
  },
  subtitle: {
    fontSize: '0.875rem',
    color: '#666',
    margin: 0,
  },
  filterRow: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1rem',
  },
  filterButton: {
    flex: 1,
    padding: '0.5rem',
    fontSize: '0.8rem',
    fontWeight: 500,
    backgroundColor: '#f0f0f0',
    color: '#666',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  filterButtonActive: {
    backgroundColor: '#646cff',
    color: '#fff',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    padding: '1rem',
    cursor: 'pointer',
    transition: 'transform 0.15s, box-shadow 0.15s',
  },
  cardHeader: {
    marginBottom: '0.75rem',
  },
  cardTitleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.5rem',
  },
  typeBadge: {
    fontSize: '0.75rem',
  },
  statusBadge: {
    fontSize: '0.7rem',
    fontWeight: 600,
    padding: '0.2rem 0.6rem',
    borderRadius: '12px',
  },
  cardTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#1a1a1a',
    margin: 0,
  },
  cardFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.75rem',
    color: '#999',
  },
  cardMeta: {
    direction: 'ltr',
  },
};
